import { useState, memo, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  FaBolt,
  FaTemperatureHigh,
  FaArrowRight,
  FaRuler,
  FaWeight,
  FaInfoCircle,
  FaTable,
  FaSolarPanel,
  FaChartLine,
  FaWater,
  FaExchangeAlt,
  FaLeaf,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SpecItem = memo(({ spec, activeTab }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`flex items-center gap-3 p-4 rounded-lg ${
      activeTab === "electricity" ? "bg-blue-900/10" : "bg-red-900/10"
    }`}
  >
    <div className="text-2xl">{spec.icon}</div>
    <div>
      <div className="text-gray-400 text-sm">{spec.name}</div>
      <div className="text-white font-semibold">{spec.value}</div>
    </div>
  </motion.div>
));

SpecItem.displayName = "SpecItem";

SpecItem.propTypes = {
  spec: PropTypes.shape({
    icon: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  activeTab: PropTypes.string.isRequired,
};

const PVTSpecifications = ({ detailData }) => {
  const { t } = useTranslation();
  const pvtSpecsData = t("components.pvt.pvtSpecifications", {
    returnObjects: true,
  });
  const [activeTab, setActiveTab] = useState("electricity");

  const handleElectricityTab = useCallback(() => {
    setActiveTab("electricity");
  }, []);

  const handleThermalTab = useCallback(() => {
    setActiveTab("thermal");
  }, []);

  const photovoltaicSpecs = detailData
    ? [
        {
          name: detailData.photovoltaicSpecs.dimensions.name,
          value: detailData.photovoltaicSpecs.dimensions.value,
          icon: <FaRuler className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.weight.name,
          value: detailData.photovoltaicSpecs.weight.value,
          icon: <FaWeight className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.frameType.name,
          value: detailData.photovoltaicSpecs.frameType.value,
          icon: <FaInfoCircle className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.frontSide.name,
          value: detailData.photovoltaicSpecs.frontSide.value,
          icon: <FaInfoCircle className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.cellCount.name,
          value: detailData.photovoltaicSpecs.cellCount.value,
          icon: <FaTable className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.pvType.name,
          value: detailData.photovoltaicSpecs.pvType.value,
          icon: <FaSolarPanel className="text-blue-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.maxPower.name,
          value: detailData.photovoltaicSpecs.maxPower.value,
          icon: <FaBolt className="text-yellow-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.efficiency.name,
          value: detailData.photovoltaicSpecs.efficiency.value,
          icon: <FaChartLine className="text-green-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.currentTempCoeff.name,
          value: detailData.photovoltaicSpecs.currentTempCoeff.value,
          icon: <FaTemperatureHigh className="text-red-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.powerTempCoeff.name,
          value: detailData.photovoltaicSpecs.powerTempCoeff.value,
          icon: <FaTemperatureHigh className="text-red-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.voltageTempCoeff.name,
          value: detailData.photovoltaicSpecs.voltageTempCoeff.value,
          icon: <FaTemperatureHigh className="text-red-400" />,
        },
        {
          name: detailData.photovoltaicSpecs.grossArea.name,
          value: detailData.photovoltaicSpecs.grossArea.value,
          icon: <FaRuler className="text-blue-400" />,
        },
      ]
    : [];

  const thermalSpecs = detailData
    ? [
        {
          name: detailData.thermalSpecs.thermalPower.name,
          value: detailData.thermalSpecs.thermalPower.value,
          icon: <FaTemperatureHigh className="text-red-400" />,
        },
        {
          name: detailData.thermalSpecs.connections.name,
          value: detailData.thermalSpecs.connections.value,
          icon: <FaExchangeAlt className="text-blue-400" />,
        },
        {
          name: detailData.thermalSpecs.mediumType.name,
          value: detailData.thermalSpecs.mediumType.value,
          icon: <FaWater className="text-blue-400" />,
        },
        {
          name: detailData.thermalSpecs.mediumQuantity.name,
          value: detailData.thermalSpecs.mediumQuantity.value,
          icon: <FaWater className="text-blue-400" />,
        },
        {
          name: detailData.thermalSpecs.absorberSheet.name,
          value: detailData.thermalSpecs.absorberSheet.value,
          icon: <FaInfoCircle className="text-blue-400" />,
        },
        {
          name: detailData.thermalSpecs.register.name,
          value: detailData.thermalSpecs.register.value,
          icon: <FaInfoCircle className="text-blue-400" />,
        },
        {
          name: detailData.thermalSpecs.insulation.name,
          value: detailData.thermalSpecs.insulation.value,
          icon: <FaLeaf className="text-green-400" />,
        },
      ]
    : [];

  if (photovoltaicSpecs.length === 0 && thermalSpecs.length === 0) {
    return null;
  }

  const activeSpecs =
    activeTab === "electricity" ? photovoltaicSpecs : thermalSpecs;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "0px 0px -100px 0px" }}
      id="safePad"
    >
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-xl p-6 shadow-xl">
        <h2 className="text-xl md:text-2xl font-bold mb-8 text-center">
          {pvtSpecsData.title}
        </h2>

        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 bg-gray-800 rounded-lg">
            <button
              onClick={handleElectricityTab}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "electricity"
                  ? "bg-blue-600 text-white"
                  : "hover:bg-gray-700 text-gray-300 cursor-pointer"
              }`}
            >
              <FaBolt className="inline mr-1" />
              {pvtSpecsData.tabs.photovoltaic}
            </button>
            <button
              onClick={handleThermalTab}
              className={`px-4 py-2 rounded-lg transition-all ${
                activeTab === "thermal"
                  ? "bg-red-600 text-white"
                  : "hover:bg-gray-700 text-gray-300 cursor-pointer"
              }`}
            >
              <FaTemperatureHigh className="inline mr-1" />
              {pvtSpecsData.tabs.thermal}
            </button>
          </div>
        </div>

        <motion.div
          key={activeTab}
          variants={contentVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 gap-4"
        >
          {activeSpecs.map((spec, index) => (
            <SpecItem key={index} spec={spec} activeTab={activeTab} />
          ))}
        </motion.div>

        <div className="mt-12 text-center">
          <Link to="/contact" className="inline-block">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium px-8 py-3 rounded-lg shadow-lg flex items-center mx-auto cursor-pointer"
            >
              {pvtSpecsData.cta}
              <FaArrowRight className="ml-2" />
            </motion.button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default memo(PVTSpecifications);

PVTSpecifications.propTypes = {
  detailData: PropTypes.shape({
    photovoltaicSpecs: PropTypes.shape({
      dimensions: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      weight: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      frameType: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      frontSide: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      cellCount: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      pvType: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      maxPower: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      efficiency: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      currentTempCoeff: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      powerTempCoeff: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      voltageTempCoeff: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      grossArea: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    thermalSpecs: PropTypes.shape({
      thermalPower: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      connections: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      mediumType: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      mediumQuantity: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      absorberSheet: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      register: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
      insulation: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};
